import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
  writeBatch,
  getFirestore
} from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import { toast, ToastContainer } from 'react-toastify';
import { MessageCircle, Trash2, AlertCircle, Share2 } from 'lucide-react';
// Mocking Firebase config for the preview environment
import { auth, db } from '../FirebaseConfig';
const noImg = "https://placehold.co/400x400/1e293b/ffffff?text=No+Image";
const emailjs = { send: async () => true };
const sendEmail = async () => true;
// ---------------------------------------------------------

const Icons = {
  users: '👥',
  products: '📦',
  orders: '🛒',
  food: '🍔',
  service: '🔧',
  listing: '🏠',
  promotion: '📢',
  block: '🚫',
  verify: '✓',
  unverify: '⚠️',
  stats: '📊',
  search: '🔍',
  filter: '⊙',
  warn: '❗',
  expand: '🔽',
  delete: '🗑️',
  whatsapp: '💬',
};

// Helper function to format phone number for WhatsApp
const formatWhatsAppPhone = (phone) => {
  if (!phone) return null;
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Handle Nigerian numbers (starting with 0 or 234)
  if (digits.startsWith('234')) {
    return digits; // Already in correct format: 2348012345678
  } else if (digits.startsWith('0')) {
    return '234' + digits.slice(1); // Convert 08012345678 to 2348012345678
  } else if (digits.length === 10) {
    return '234' + digits; // Assume Nigerian number without country code
  }
  
  return digits; // Return as-is if format unknown
};

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ user, onConfirm, onCancel, isDeleting }) => {
  const [confirmText, setConfirmText] = useState('');
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-red-600/50 rounded-2xl p-8 max-w-md w-full shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle size={32} className="text-red-500" />
          <h2 className="text-2xl font-bold text-white">⚠️ Delete User?</h2>
        </div>
        
        <div className="space-y-4 mb-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-300 text-sm font-bold">This action CANNOT be undone!</p>
          <p className="text-slate-300 text-sm">
            Deleting will permanently remove:
          </p>
          <ul className="text-slate-400 text-xs space-y-1 ml-4 mt-2">
            <li>✗ User account ({user.name})</li>
            <li>✗ All products listed</li>
            <li>✗ All orders placed</li>
            <li>✗ All referral data</li>
            <li>✗ All profile information</li>
          </ul>
        </div>

        <div className="mb-6">
          <label className="text-slate-400 text-sm font-bold block mb-2">
            Type user name "{user.name}" to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Enter exact name..."
            className="w-full bg-slate-800 border border-red-600/50 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-red-600 placeholder-slate-600"
          />
          <p className="text-slate-500 text-xs mt-2">
            Match: {confirmText === user.name ? (
              <span className="text-emerald-400 font-bold">✓ Match!</span>
            ) : (
              <span className="text-red-400">✗ No match</span>
            )}
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (confirmText === user.name) {
                onConfirm();
              } else {
                toast.error("Name does not match. Try again.");
              }
            }}
            disabled={confirmText !== user.name || isDeleting}
            className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition font-bold flex items-center justify-center gap-2"
          >
            {isDeleting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={16} /> DELETE
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminProductForm = ({ isSigned, adminEmails, userEmail }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [services, setServices] = useState([]);
  const [listings, setListings] = useState([]);
  const [foodItems, setFoodItems] = useState([]);
  const [promotion, setPromotion] = useState([]);
  const [users, setUsers] = useState([]);
  
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // NEW STATE: Tracks which product is currently being sent to WhatsApp so we can show a loading spinner
  const [broadcastingId, setBroadcastingId] = useState(null);

  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    buyerCount: 0,
    sellerCount: 0,
    unverifiedSellers: 0,
    inactiveSellers: 0,
    verifiedInactiveSellers: 0,
    inactiveBuyers: 0,
    blockedUsers: 0,
    usersWithReferralCode: 0,
  });
  
  const [userType, setUserType] = useState("all");
  const [promoStatusFilter, setPromoStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!adminEmails?.includes(userEmail)) {
      navigate('/');
      toast.error("You do not have access to this page.");
    }
  }, [adminEmails, userEmail, navigate]);

  useEffect(() => {
    if (!isSigned) {
      setTimeout(() => navigate('/login'), 3000);
    }
  }, [isSigned, navigate]);

  const sendAdminEmail = async (toEmail, subject, message) => {
    if (!toEmail) return false;
    try {
      await emailjs.send(
        'service_owcllxy',
        'template_w29niag',
        {
          to_email: toEmail,
          subject: subject,
          message: message,
        },
        "PdbHvZLbWntsrX1u8"
      );
      return true;
    } catch (err) {
      console.error("Email send error:", err);
      return false;
    }
  };

  // ========================================
  // BROADCAST TO WHATSAPP FUNCTION
  // ========================================
  const handleBroadcast = async (product) => {
    setBroadcastingId(product.id); // Show spinner on this specific button
    try {
      const response = await fetch('http://localhost:3000/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productName: product.name,
          price: product.price || 0,
          storeName: product.storeName || 'uCart Seller',
          link: `https://ucart.com/product/${product.id}`,
          imageUrl: product.imageUrl || noImg // We send the image link to the bot here!
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        toast.success(`Sent ${product.name} to WhatsApp Group!`);
      } else {
        toast.error(`Bot Error: ${data.message || 'Group not found'}`);
      }
    } catch (error) {
      console.error('Broadcast error:', error);
      toast.error('Could not connect to WhatsApp Bot. Is the server running?');
    } finally {
      setBroadcastingId(null); // Stop the spinner
    }
  };

  // ========================================
  // DELETE USER WITH CASCADE DELETE
  // ========================================
  const handleDeleteUser = async (userId) => {
    setIsDeleting(true);
    try {
      const userDoc = await getDocs(
        query(collection(db, 'profiles'), where('__name__', '==', userId))
      );

      const productsQ = query(
        collection(db, "Products"),
        where("sellerId", "==", userId)
      );
      const productsSnap = await getDocs(productsQ);
      
      const batch1 = writeBatch(db);
      productsSnap.docs.forEach(doc => {
        batch1.delete(doc.ref);
      });
      if (productsSnap.docs.length > 0) {
        await batch1.commit();
      }

      const ordersQ = query(
        collection(db, "Orders"),
        where("buyerId", "==", userId)
      );
      const ordersSnap = await getDocs(ordersQ);
      
      const batch2 = writeBatch(db);
      ordersSnap.docs.forEach(doc => {
        batch2.delete(doc.ref);
      });
      if (ordersSnap.docs.length > 0) {
        await batch2.commit();
      }

      await deleteDoc(doc(db, 'profiles', userId));

      toast.error("✓ User and all associated data permanently deleted.");
      setUserToDelete(null);
      await fetchUsers();
    } catch (error) {
      toast.error(`Failed to delete user: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // ========================================
  // WHATSAPP INTEGRATION (Direct Chat)
  // ========================================
  const openWhatsApp = (phoneNumber, userName) => {
    const formattedPhone = formatWhatsAppPhone(phoneNumber);
    
    if (!formattedPhone) {
      toast.error(`❌ No valid phone number for ${userName}`);
      return;
    }

    const message = encodeURIComponent(`Hello ${userName}, this is a message from uCart Admin.`);
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${message}`;
    
    window.open(whatsappUrl, '_blank');
    toast.success(`📱 Opening WhatsApp conversation with ${userName}...`);
  };

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const profilesRef = collection(db, "profiles");
      const profilesSnapshot = await getDocs(profilesRef);
      
      const allProfiles = profilesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      let totalUsers = allProfiles.length;
      let buyerCount = allProfiles.filter(u => u.role === "buyer").length;
      let sellerCount = allProfiles.filter(u => u.role === "seller").length;
      let unverifiedSellers = allProfiles.filter(u => u.role === "seller" && u.verification_status !== 'approved').length;
      let blockedUsers = allProfiles.filter(u => u.blocked === true).length;
      let inactiveSellers = allProfiles.filter(u => u.role === "seller" && !u.hasListed).length;
      
      let verifiedInactiveSellers = allProfiles.filter(u => 
        u.role === "seller" && 
        (u.verified === true || u.verification_status === 'approved') && 
        !u.hasListed
      ).length;
      
      let inactiveBuyers = allProfiles.filter(u => u.role === "buyer" && !u.hasPurchased).length;
      let usersWithReferralCode = allProfiles.filter(u => u.referralCode && u.referralCode.trim() !== '').length;

      setUserStats({
        totalUsers,
        buyerCount,
        sellerCount,
        unverifiedSellers,
        inactiveSellers,
        verifiedInactiveSellers,
        inactiveBuyers,
        blockedUsers,
        usersWithReferralCode,
      });

      let filteredUsers = allProfiles;
      if (userType === "buyer" || userType === "seller") {
        filteredUsers = allProfiles.filter(u => u.role === userType);
      }

      setUsers(filteredUsers);
    } catch (error) {
      toast.error("Failed to fetch user list.");
    } finally {
      setIsLoading(false);
    }
  }, [userType]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      let data;
      let ref;

      if (activeTab === 'orders') {
        ref = collection(db, 'Orders');
        const snapshot = await getDocs(ref);
        data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOrders(data);
      } else if (activeTab === 'promotion') {
        ref = collection(db, 'promotions');
        const promoQuery = promoStatusFilter === 'all'
          ? ref
          : query(ref, where('status', '==', promoStatusFilter));
        const snapshot = await getDocs(promoQuery);
        data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPromotion(data);
      } else if (activeTab === 'service') {
        ref = collection(db, 'services');
        const snapshot = await getDocs(ref);
        data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setServices(data);
      } else if (activeTab === 'listing') {
        ref = collection(db, 'listings');
        const snapshot = await getDocs(ref);
        data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setListings(data);
      } else if (activeTab === 'food') {
        ref = collection(db, 'foodItems');
        const snapshot = await getDocs(ref);
        data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFoodItems(data);
      } else if (['all', 'pending', 'approved'].includes(activeTab)) {
        let productsQuery = collection(db, 'Products');
        if (activeTab === 'pending') {
          productsQuery = query(productsQuery, where('approved', '==', false));
        } else if (activeTab === 'approved') {
          productsQuery = query(productsQuery, where('approved', '==', true));
        }
        const snapshot = await getDocs(productsQuery);
        data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProducts(data);
      }
    } catch (err) {
      toast.error(`Failed to fetch data for ${activeTab}.`);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, promoStatusFilter]);

  useEffect(() => {
    if (activeTab === 'users' || activeTab === 'dashboard') {
      fetchUsers();
    } 
  }, [activeTab, userType, fetchUsers]);

  useEffect(() => {
    if (activeTab !== 'users' && activeTab !== 'dashboard') {
      fetchData();
    }
  }, [activeTab, promoStatusFilter, fetchData]);

  const handleVerifyUser = async (userId) => {
    try {
      await updateDoc(doc(db, 'profiles', userId), { 
        verified: true, 
        verification_status: 'approved' 
      });
      toast.success("User verified!");
      await fetchUsers();
    } catch (err) {
      toast.error("Failed to verify user.");
    }
  };

  const handleUnverifyUser = async (userId) => {
    try {
      await updateDoc(doc(db, 'profiles', userId), { 
        verified: false, 
        verification_status: 'pending' 
      });
      toast.info("User verification removed.");
      await fetchUsers();
    } catch (err) {
      toast.error("Failed to remove verification.");
    }
  };

// ================================================================
// PASTE THIS into AdminProductForm.jsx
// Replace your existing handleProductApprove function with this one.
// It now AUTO-BROADCASTS to WhatsApp the moment you approve a product.
// ================================================================

const handleProductApprove = async (id, email) => {
  try {
    await updateDoc(doc(db, 'Products', id), { approved: true });

    if (email) {
      await sendEmail(
        email,
        'Product Approved',
        'Your product has been approved and is now visible on the platform.'
      );
    }

    toast.success("Product approved!");

    // ── AUTO-BROADCAST TO WHATSAPP ──────────────────────────────
    // Find the product we just approved so we can get its details
    const approvedProduct = products.find(p => p.id === id);

    if (approvedProduct) {
      try {
        const response = await fetch('http://localhost:3001/broadcast', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productName:  approvedProduct.name,
            price:        approvedProduct.price || 0,
            storeName:    approvedProduct.storeName   || 'uCart Seller',
            category:     approvedProduct.category    || '',
            university:   approvedProduct.university  || '',
            link:         `https://ucartstore.xyz/product/${approvedProduct.id}`,
            imageUrl:     approvedProduct.imageUrl    || null,
          }),
        });

        const data = await response.json();

        if (data.status === 'success') {
          toast.success(`📲 "${approvedProduct.name}" sent to WhatsApp group!`);
        } else {
          // Don't block the approval flow — just warn the admin
          toast.warn(`⚠️ Approved but WhatsApp send failed: ${data.message}`);
        }
      } catch (broadcastErr) {
        // Bot server might be offline — approval still succeeds
        console.warn('WhatsApp broadcast failed:', broadcastErr.message);
        toast.warn('⚠️ Approved! But WhatsApp bot is offline.');
      }
    }
    // ────────────────────────────────────────────────────────────

    await fetchData();
  } catch (err) {
    toast.error("Failed to approve product.");
  }
};

  const handleProductReject = async (id, email) => {
    try {
      await updateDoc(doc(db, 'Products', id), { approved: false });
      if (email) {
        await sendEmail(email, 'Product Rejected', 'Your product does not meet our guidelines. Please review and resubmit.');
      }
      toast.info("Product rejected.");
      await fetchData();
    } catch (err) {
      toast.error("Failed to reject product.");
    }
  };

  const handleProductDelete = async (id, email) => {
    try {
      await deleteDoc(doc(db, 'Products', id));
      if (email) {
        await sendEmail(email, 'Product Deleted', 'Your product has been deleted from the platform.');
      }
      toast.warn("Product deleted.");
      await fetchData();
    } catch (err) {
      toast.error("Failed to delete product.");
    }
  };

  const handleWarnUser = async (email, userName) => {
    if (!email) {
      toast.error(`No email found for ${userName}`);
      return;
    }
    const success = await sendAdminEmail(
      email,
      'Official Account Warning - uCart',
      `Hello ${userName},\n\nThis is an official warning from uCart Administration.\nContinued violations will result in account suspension.\n\nPlease review our policies.`
    );
    success ? toast.success(`Warning sent to ${userName}`) : toast.error("Failed to send warning.");
  };

  const handleBlockUser = async (userId, userName) => {
    try {
      await updateDoc(doc(db, 'profiles', userId), { blocked: true });
      const targetUser = users.find(u => u.id === userId);
      const email = targetUser?.userEmail || targetUser?.email || targetUser?.Email;
      if (email) {
        await sendAdminEmail(
          email,
          'Account Suspended - uCart',
          `Hello ${userName},\n\nYour uCart account has been blocked due to policy violations.\nContact admin if you believe this was a mistake.`
        );
      }
      toast.warn(`${userName} has been blocked.`);
      await fetchUsers();
    } catch (err) {
      toast.error("Failed to block user.");
    }
  };

  const handleUnblockUser = async (userId, userName) => {
    try {
      await updateDoc(doc(db, 'profiles', userId), { blocked: false });
      const targetUser = users.find(u => u.id === userId);
      const email = targetUser?.userEmail || targetUser?.email || targetUser?.Email;
      if (email) {
        await sendAdminEmail(
          email,
          'Account Reopened - uCart',
          `Hello ${userName},\n\nYour uCart account has been restored.\nPlease ensure compliance with platform rules.`
        );
      }
      toast.success(`${userName} has been unblocked.`);
      await fetchUsers();
    } catch (err) {
      toast.error("Failed to unblock user.");
    }
  };

  const handleSendNotification = async (email, type) => {
    if (!email) {
      toast.error("No email address found.");
      return;
    }
    const isSeller = type === 'seller';
    const subject = isSeller ? 'Get Started Selling on uCart!' : 'New Products Waiting For You!';
    const message = isSeller 
      ? "We noticed you haven't listed any products yet. Start selling today and reach thousands of buyers!"
      : "Hey there! Come check out the latest products and great deals on uCart.";

    const success = await sendAdminEmail(email, subject, message);
    success ? toast.success(`Notification sent!`) : toast.error("Failed to send notification.");
  };

  const toggleUserDetails = (id) => {
    setExpandedUserId(prev => prev === id ? null : id);
  };

  const handleFoodApprove = async (id) => {
    try {
      await updateDoc(doc(db, 'foodItems', id), { approved: true });
      toast.success("Food item approved!");
      await fetchData();
    } catch (err) {
      toast.error("Failed to approve food item.");
    }
  };

  const handleFoodDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'foodItems', id));
      toast.warn("Food item deleted.");
      await fetchData();
    } catch (err) {
      toast.error("Failed to delete food item.");
    }
  };

  const handleApprovePromotion = async (id) => {
    try {
      await updateDoc(doc(db, "promotions", id), { status: "approved" });
      toast.success("Promotion approved!");
      await fetchData();
    } catch (error) {
      toast.error("Failed to approve promotion.");
    }
  };

  const handleDeletePromotion = async (id) => {
    try {
      await deleteDoc(doc(db, 'promotions', id));
      toast.warn("Promotion deleted.");
      await fetchData();
    } catch (err) {
      toast.error("Failed to delete promotion.");
    }
  };

  const handleServiceDelete = async (id, email) => {
    try {
      await deleteDoc(doc(db, 'services', id));
      if (email) {
        await sendEmail(email, 'Service Deleted', 'Your service has been deleted from the platform.');
      }
      toast.warn("Service deleted.");
      await fetchData();
    } catch (err) {
      toast.error("Failed to delete service.");
    }
  };

  const handlePropertyDelete = async (id, email) => {
    try {
      await deleteDoc(doc(db, 'listings', id));
      if (email) {
        await sendEmail(email, 'Property Listing Deleted', 'Your property listing has been deleted from the platform.');
      }
      toast.warn("Listing deleted.");
      await fetchData();
    } catch (err) {
      toast.error("Failed to delete listing.");
    }
  };

  const handleOrderAction = async (id, status, email) => {
    try {
      const updateFields = { status: status };
      if (status === 'approved') {
        updateFields.payoutDone = true;
      }
      await updateDoc(doc(db, 'Orders', id), updateFields);
      
      if (email) {
        const emailSubject = `Order ${status === 'approved' ? 'Approved' : 'Rejected'}`;
        const emailMessage = `Your order has been ${status}.`;
        await sendEmail(email, emailSubject, emailMessage);
      }
      
      toast.success(`Order ${status}.`);
      await fetchData();
    } catch (error) {
      toast.error("Failed to update order status.");
    }
  };

  const handleDeleteOrder = async (id, email) => {
    try {
      await deleteDoc(doc(db, 'Orders', id));
      if (email) {
        await sendEmail(email, 'Order Deleted', 'Your order has been deleted.');
      }
      toast.warn("Order deleted.");
      await fetchData();
    } catch (err) {
      toast.error("Failed to delete order.");
    }
  };

  const batchUpdateUniversities = async () => {
    try {
      toast.info("Starting batch update...");
      const profilesSnapshot = await getDocs(collection(db, 'profiles'));
      const profiles = profilesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      let totalUpdated = 0;

      for (const profile of profiles) {
        if (!profile.university) continue;

        const productsQuery = query(
          collection(db, 'Products'),
          where('sellerId', '==', profile.id)
        );
        const productsSnapshot = await getDocs(productsQuery);

        if (productsSnapshot.empty) continue;

        const missingUniProducts = productsSnapshot.docs.filter(doc => {
          const data = doc.data();
          return !data.university || data.university.trim() === '';
        });

        if (missingUniProducts.length === 0) continue;

        const batch = writeBatch(db);
        missingUniProducts.forEach((productDoc) => {
          batch.update(productDoc.ref, { university: profile.university });
        });

        await batch.commit();
        totalUpdated += missingUniProducts.length;
      }

      toast.success(`Batch update complete! Updated ${totalUpdated} products.`);
      await fetchData();
    } catch (error) {
      toast.error("Failed to batch update universities.");
    }
  };

  const filteredUsers = useMemo(() => {
    let filtered = users.filter(u =>
      (u.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (u.userEmail?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (u.Email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (u.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (u.referralCode?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (verificationFilter === 'verified') {
      filtered = filtered.filter(u => u.verified === true || u.verification_status === 'approved');
    } else if (verificationFilter === 'unverified') {
      filtered = filtered.filter(u => u.verification_status !== 'approved');
    } else if (verificationFilter === 'blocked') {
      filtered = filtered.filter(u => u.blocked === true);
    } else if (verificationFilter === 'inactiveBuyer') {
      filtered = filtered.filter(u => u.role === 'buyer' && !u.hasPurchased); 
    } else if (verificationFilter === 'verifiedInactiveSeller') {
      filtered = filtered.filter(u => u.role === 'seller' && (u.verified === true || u.verification_status === 'approved') && !u.hasListed);
    } else if (verificationFilter === 'withReferralCode') {
      filtered = filtered.filter(u => u.referralCode && u.referralCode.trim() !== '');
    }

    return filtered;
  }, [users, searchTerm, verificationFilter]);

  const filteredProducts = useMemo(() =>
    products.filter(p =>
      p.name?.toLowerCase().includes(searchTerm.toLowerCase())
    ), [products, searchTerm]);

  const filteredFoodItems = useMemo(() =>
    foodItems.filter(f =>
      f.name?.toLowerCase().includes(searchTerm.toLowerCase())
    ), [foodItems, searchTerm]);


  const DashboardHeader = () => (
    <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700 sticky top-0 z-40 backdrop-blur-md bg-opacity-90">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
              {Icons.stats} Admin Dashboard
            </h1>
            <p className="text-slate-400 text-sm mt-2">Manage products, orders, users, and promotions</p>
          </div>

          <Link
            to="/AdminVerify"
            className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all duration-200 font-semibold shadow-lg hover:shadow-emerald-500/50"
          >
            {Icons.verify} Verification System
          </Link>
        </div>
      </div>
    </div>
  );

  const NavigationTabs = () => (
    <div className="bg-slate-800 border-b border-slate-700 sticky top-16 z-30 overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex gap-2 overflow-x-auto pb-0 scrollbar-hide">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: '📊' },
            { id: 'users', label: 'Users', icon: '👥' },
            { id: 'pending', label: 'Pending Products', icon: '📦' },
            { id: 'approved', label: 'Approved Products', icon: '✅' },
            { id: 'all', label: 'All Products', icon: '📦' },
            { id: 'orders', label: 'Orders', icon: '🛒' },
            { id: 'food', label: 'Food Items', icon: '🍔' },
            { id: 'service', label: 'Services', icon: '🔧' },
            { id: 'listing', label: 'Listings', icon: '🏠' },
            { id: 'promotion', label: 'Promotions', icon: '📢' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-all duration-200 border-b-2 ${
                activeTab === tab.id
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const DashboardOverview = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {[
        { label: 'Total Users', value: userStats.totalUsers, textColor: 'text-emerald-400', icon: '👥' },
        { label: 'Total Sellers', value: userStats.sellerCount, textColor: 'text-blue-400', icon: '🏪' },
        { label: 'Total Buyers', value: userStats.buyerCount, textColor: 'text-purple-400', icon: '🛍️' },
        { label: 'Unverified Sellers', value: userStats.unverifiedSellers, textColor: 'text-amber-400', icon: '⚠️' },
        { label: 'Verified Inactive Sellers', value: userStats.verifiedInactiveSellers, textColor: 'text-teal-400', icon: '😴' },
        { label: 'Inactive Buyers', value: userStats.inactiveBuyers, textColor: 'text-orange-400', icon: '🛒' },
        { label: 'Blocked Users', value: userStats.blockedUsers, textColor: 'text-rose-500', icon: '🚫' },
        { label: 'Users with Referral Code', value: userStats.usersWithReferralCode, textColor: 'text-pink-400', icon: '🎁' },
      ].map((stat, idx) => (
        <div
          key={idx}
          className={`bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-5 hover:border-slate-600 transition-all duration-200 shadow-lg`}
        >
          <p className="text-slate-400 text-sm font-medium mb-2">{stat.icon} {stat.label}</p>
          <p className={`text-3xl font-bold ${stat.textColor}`}>
            {stat.value}
          </p>
        </div>
      ))}
    </div>
  );

  const UsersSection = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="text-slate-400 text-sm font-semibold block mb-2">Filter by Role</label>
            <select
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Users</option>
              <option value="buyer">Buyers</option>
              <option value="seller">Sellers</option>
            </select>
          </div>

          <div>
            <label className="text-slate-400 text-sm font-semibold block mb-2">Advanced Filter</label>
            <select
              value={verificationFilter}
              onChange={(e) => setVerificationFilter(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Statuses</option>
              <option value="verified">Verified Users</option>
              <option value="unverified">Unverified Users</option>
              <option value="blocked">Blocked Users</option>
              <option value="inactiveBuyer">Buyers (No Purchases)</option>
              <option value="verifiedInactiveSeller">Verified Sellers (No Listings)</option>
              <option value="withReferralCode">Users with Referral Code</option>
            </select>
          </div>

          <div>
            <label className="text-slate-400 text-sm font-semibold block mb-2">Search</label>
            <input
              type="text"
              placeholder="Search by name, email, or referral code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 placeholder-slate-600"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-slate-400">Loading users...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 bg-slate-800 rounded-xl border border-slate-700">
          <p className="text-slate-400">No users found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredUsers.map(user => {
            const resolvedEmail = user.userEmail || user.Email || user.email;
            const resolvedPhone = user.phoneNumber || user.phone;

            return (
              <div
                key={user.id}
                className={`bg-gradient-to-br from-slate-800 to-slate-900 border rounded-xl p-6 shadow-lg transition-all duration-200 flex flex-col justify-between ${
                  user.blocked ? 'border-rose-600/50 bg-rose-950/20' : 'border-slate-700 hover:border-slate-600'
                }`}
              >
                <div>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        {user.name || "No Name"}
                        {(user.verified || user.verification_status === 'approved') && <span className="text-emerald-400 text-lg" title="Verified">✓</span>}
                        {user.blocked && <span className="text-rose-400 text-lg" title="Blocked">🔒</span>}
                      </h3>
                      
                      {user.role === 'seller' ? (
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="bg-slate-800 text-slate-300 text-xs px-2.5 py-1 rounded-md border border-slate-700">
                            🏪 {user.storeName || "No Store"}
                          </span>
                          <span className="bg-slate-800 text-slate-300 text-xs px-2.5 py-1 rounded-md border border-slate-700">
                            🎓 {user.university || "No Uni"}
                          </span>
                        </div>
                      ) : (
                        <span className="inline-block mt-2 bg-slate-800 text-slate-300 text-xs px-2.5 py-1 rounded-md border border-slate-700">
                          🛍️ Buyer
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 mb-4 text-sm text-slate-300">
                    <p><strong>Email:</strong> {resolvedEmail || "Not Registered"}</p> 
                    <p><strong>Phone:</strong> {resolvedPhone || "N/A"}</p>
                    
                    {user.referralCode && (
                      <p><strong>Referral Code:</strong> <span className="text-pink-400 font-semibold">{user.referralCode}</span></p>
                    )}
                    
                    <button 
                      onClick={() => toggleUserDetails(user.id)}
                      className="text-emerald-400 hover:text-emerald-300 text-xs mt-3 font-semibold flex items-center gap-1"
                    >
                      {expandedUserId === user.id ? 'Hide Details' : 'See More Details'} {Icons.expand}
                    </button>
                    
                    {expandedUserId === user.id && (
                      <div className="bg-slate-900 p-4 rounded-lg mt-3 border border-slate-700 space-y-1.5">
                        <p><strong>Database ID:</strong> {user.id}</p>
                        <p><strong>Referral Code:</strong> {user.referralCode || "N/A"}</p>
                        <p className="text-emerald-300"><strong>Total Earnings:</strong> ₦{user.totalEarnings || 0}</p>
                        {user.role === 'seller' && (
                          <>
                            <p><strong>Listings Made:</strong> {user.hasListed ? 'Yes' : 'No'}</p>
                            {user.accountNumber && <p><strong>Account:</strong> {user.accountNumber}</p>}
                            {user.bankName && <p><strong>Bank:</strong> {user.bankName}</p>}
                          </>
                        )}
                        {user.role === 'buyer' && (
                          <p><strong>Purchases Made:</strong> {user.hasPurchased ? 'Yes' : 'No'}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-700">
                  {(!user.verified && user.verification_status !== 'approved') && (
                    <button
                      onClick={() => handleVerifyUser(user.id)}
                      className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg transition font-semibold"
                    >
                      {Icons.verify} Verify
                    </button>
                  )}
                  {(user.verified || user.verification_status === 'approved') && (
                    <button
                      onClick={() => handleUnverifyUser(user.id)}
                      className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg transition"
                    >
                      Unverify
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleWarnUser(resolvedEmail, user.name)}
                    className="text-xs bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-lg transition"
                  >
                    {Icons.warn} Warn
                  </button>

                  {!user.blocked ? (
                    <button
                      onClick={() => handleBlockUser(user.id, user.name)}
                      className="text-xs bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-lg transition"
                    >
                      {Icons.block} Block
                    </button>
                  ) : (
                    <button
                      onClick={() => handleUnblockUser(user.id, user.name)}
                      className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition"
                    >
                      Unblock
                    </button>
                  )}

                  {resolvedPhone && (
                    <button
                      onClick={() => openWhatsApp(resolvedPhone, user.name)}
                      className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg transition flex items-center gap-1 font-semibold"
                      title="Send WhatsApp message"
                    >
                      <MessageCircle size={14} /> Chat
                    </button>
                  )}
                  
                  {user.role === 'seller' && (
                    <button
                      onClick={() => handleSendNotification(resolvedEmail, 'seller')}
                      className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg transition"
                    >
                      📧 Notify
                    </button>
                  )}

                  <button
                    onClick={() => setUserToDelete(user)}
                    className="text-xs bg-red-700 hover:bg-red-800 text-white px-3 py-1.5 rounded-lg transition flex items-center gap-1 font-semibold"
                    title="Permanently delete user and all data"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const ProductsSection = () => (
    <div className="space-y-6">
      {['all', 'pending', 'approved'].includes(activeTab) && (
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
          <button
            onClick={batchUpdateUniversities}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg transition font-semibold"
          >
            Batch Update Product Universities
          </button>
        </div>
      )}

      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-6">
        <input
          type="text"
          placeholder="Search products by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 placeholder-slate-600"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-slate-400">Loading products...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-slate-800 rounded-xl border border-slate-700">
          <p className="text-slate-400">No products to display.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredProducts.map(product => (
            <div
              key={product.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl overflow-hidden shadow-lg hover:border-slate-600 transition-all duration-200"
            >
              {product.imageUrl && (
                <img
                  src={product.imageUrl || noImg}
                  alt={product.name}
                  className="w-full h-40 object-cover"
                />
              )}
              <div className="p-5">
                <h3 className="text-lg font-bold text-white mb-2">{product.name}</h3>
                <div className="space-y-1.5 text-sm text-slate-300 mb-4">
                  <p><strong>Price:</strong> ₦{product.price?.toLocaleString() || 'N/A'}</p>
                  <p><strong>Category:</strong> {product.category || 'N/A'}</p>
                  <p><strong>University:</strong> {product.university || 'N/A'}</p>
                  <p><strong>Store:</strong> {product.storeName || 'N/A'}</p>
                  <p><strong>Seller:</strong> {product.createdBy || 'N/A'}</p>
                  <p><strong>Status:</strong> <span className={product.approved ? 'text-emerald-400' : 'text-amber-400'}>
                    {product.approved ? '✓ Approved' : '⏳ Pending'}
                  </span></p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {!product.approved && (
                    <button
                      onClick={() => handleProductApprove(product.id, product.email)}
                      className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg transition font-semibold"
                    >
                      ✓ Approve
                    </button>
                  )}
                  <button
                    onClick={() => handleProductDelete(product.id, product.email)}
                    className="flex-1 text-xs bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-lg transition font-semibold"
                  >
                    Delete
                  </button>
                  
                  {/* NEW: Broadcast to WhatsApp Button (Only show if product is approved) */}
                  {product.approved && (
                    <button
                      onClick={() => handleBroadcast(product)}
                      disabled={broadcastingId === product.id}
                      className="w-full flex items-center justify-center gap-2 text-xs bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-2 rounded-lg transition font-semibold mt-1"
                    >
                      {broadcastingId === product.id ? (
                        <>
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Share2 size={14} /> Send to WhatsApp Group
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const OrdersSection = () => (
    <div className="space-y-6">
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-slate-400">Loading orders...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 bg-slate-800 rounded-xl border border-slate-700">
          <p className="text-slate-400">No orders to display.</p>
        </div>
      ) : (
        orders.map(order => (
          <div
            key={order.id}
            className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-6 shadow-lg hover:border-slate-600 transition-all duration-200"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold text-white">Order #{order.id.slice(0, 8)}</h3>
                <p className="text-sm text-slate-400 mt-1">Status: <span className="text-emerald-400 font-semibold">{order.status}</span></p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-400">Delivered: {order.delivered ? '✓' : '✗'}</p>
                <p className="text-sm text-slate-400">Payout: {order.payoutDone ? '✓' : '⏳'}</p>
              </div>
            </div>

            {Array.isArray(order.items) && order.items.length > 0 && (
              <div className="bg-slate-900/50 rounded-lg p-4 mb-4 max-h-48 overflow-y-auto">
                {order.items.map((item, idx) => (
                  <div key={idx} className="mb-3 pb-3 border-b border-slate-700 last:border-0">
                    <p className="font-semibold text-white">{item.name}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-slate-400 mt-1">
                      <p>Price: ₦{item.price}</p>
                      <p>Qty: {item.quantity}</p>
                      <p>Seller: {item.sellerId}</p>
                      <p>Bank: {item.BankName}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {order.status !== 'approved' && (
                <button
                  onClick={() => handleOrderAction(order.id, 'approved', order.email)}
                  className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded-lg transition font-semibold"
                >
                  Approve
                </button>
              )}
              {order.status !== 'rejected' && (
                <button
                  onClick={() => handleOrderAction(order.id, 'rejected', order.email)}
                  className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-4 py-1.5 rounded-lg transition font-semibold"
                >
                  Reject
                </button>
              )}
              <button
                onClick={() => handleDeleteOrder(order.id, order.email)}
                className="text-xs bg-rose-600 hover:bg-rose-700 text-white px-4 py-1.5 rounded-lg transition font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const FoodItemsSection = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-6">
        <input
          type="text"
          placeholder="Search food items..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 placeholder-slate-600"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-slate-400">Loading food items...</p>
        </div>
      ) : filteredFoodItems.length === 0 ? (
        <div className="text-center py-12 bg-slate-800 rounded-xl border border-slate-700">
          <p className="text-slate-400">No food items to display.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredFoodItems.map(food => (
            <div
              key={food.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl overflow-hidden shadow-lg hover:border-slate-600 transition-all duration-200"
            >
              {food.imageUrl && (
                <img
                  src={food.imageUrl}
                  alt={food.name}
                  className="w-full h-40 object-cover"
                />
              )}
              <div className="p-5">
                <h3 className="text-lg font-bold text-white mb-2">{food.name}</h3>
                <div className="space-y-1.5 text-sm text-slate-300 mb-4">
                  <p><strong>Price:</strong> ₦{food.price}</p>
                  <p><strong>Prep Time:</strong> {food.time}</p>
                  <p><strong>Status:</strong> <span className={food.approved ? 'text-emerald-400' : 'text-amber-400'}>
                    {food.approved ? '✓ Approved' : '⏳ Pending'}
                  </span></p>
                </div>

                <div className="flex gap-2">
                  {!food.approved && (
                    <button
                      onClick={() => handleFoodApprove(food.id)}
                      className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg transition font-semibold"
                    >
                      Approve
                    </button>
                  )}
                  <button
                    onClick={() => handleFoodDelete(food.id)}
                    className="flex-1 text-xs bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-lg transition font-semibold"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const ServicesSection = () => (
    <div className="space-y-6">
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-slate-400">Loading services...</p>
        </div>
      ) : services.length === 0 ? (
        <div className="text-center py-12 bg-slate-800 rounded-xl border border-slate-700">
          <p className="text-slate-400">No services to display.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map(service => (
            <div
              key={service.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-6 shadow-lg hover:border-slate-600 transition-all duration-200"
            >
              <h3 className="text-lg font-bold text-white mb-2">{service.name}</h3>
              <div className="space-y-1.5 text-sm text-slate-300 mb-4">
                <p><strong>Title:</strong> {service.title}</p>
                <p><strong>Phone:</strong> {service.phone || 'N/A'}</p>
                <p><strong>Email:</strong> {service.email || 'N/A'}</p>
              </div>

              <button
                onClick={() => handleServiceDelete(service.id, service.email)}
                className="w-full text-xs bg-rose-600 hover:bg-rose-700 text-white px-4 py-1.5 rounded-lg transition font-semibold"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const ListingsSection = () => (
    <div className="space-y-6">
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-slate-400">Loading listings...</p>
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-12 bg-slate-800 rounded-xl border border-slate-700">
          <p className="text-slate-400">No listings to display.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {listings.map(listing => (
            <div
              key={listing.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-6 shadow-lg hover:border-slate-600 transition-all duration-200"
            >
              <h3 className="text-lg font-bold text-white mb-2">{listing.title || 'No Title'}</h3>
              <div className="space-y-1.5 text-sm text-slate-300 mb-4">
                <p><strong>Price:</strong> {listing.price || 'N/A'}</p>
                <p><strong>Location:</strong> {listing.location || 'Unknown'}</p>
                <p><strong>Amenities:</strong> {listing.amentities || 'Unknown'}</p>
                <p className="line-clamp-2"><strong>Description:</strong> {listing.description || 'Unknown'}</p>
              </div>

              <button
                onClick={() => handlePropertyDelete(listing.id, listing.email)}
                className="w-full text-xs bg-rose-600 hover:bg-rose-700 text-white px-4 py-1.5 rounded-lg transition font-semibold"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const PromotionsSection = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <label className="text-slate-400 text-sm font-semibold block mb-3">Filter by Status</label>
        <select
          value={promoStatusFilter}
          onChange={(e) => setPromoStatusFilter(e.target.value)}
          className="bg-slate-900 border border-slate-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-slate-400">Loading promotions...</p>
        </div>
      ) : promotion.length === 0 ? (
        <div className="text-center py-12 bg-slate-800 rounded-xl border border-slate-700">
          <p className="text-slate-400">No promotions to display.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {promotion.map(promo => (
            <div
              key={promo.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-6 shadow-lg hover:border-slate-600 transition-all duration-200"
            >
              <h3 className="text-lg font-bold text-white mb-2">{promo.caption || "No Title"}</h3>
              <div className="space-y-1.5 text-sm text-slate-300 mb-4">
                <p><strong>Amount:</strong> ₦{promo.amount}</p>
                <p><strong>Platforms:</strong> {Array.isArray(promo.platforms) ? promo.platforms.join(', ') : 'N/A'}</p>
                <p><strong>Status:</strong> <span className={promo.status === 'approved' ? 'text-emerald-400 font-semibold' : 'text-amber-400 font-semibold'}>
                  {promo.status === 'approved' ? '✓ Approved' : '⏳ Pending'}
                </span></p>
              </div>

              <div className="flex gap-2">
                {promo.status !== 'approved' && (
                  <button
                    onClick={() => handleApprovePromotion(promo.id)}
                    className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded-lg transition font-semibold"
                  >
                    Approve
                  </button>
                )}
                <button
                  onClick={() => handleDeletePromotion(promo.id)}
                  className="flex-1 text-xs bg-rose-600 hover:bg-rose-700 text-white px-4 py-1.5 rounded-lg transition font-semibold"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    if (activeTab === 'dashboard') return DashboardOverview();
    if (activeTab === 'users') return UsersSection();
    if (activeTab === 'orders') return OrdersSection();
    if (activeTab === 'promotion') return PromotionsSection();
    if (activeTab === 'service') return ServicesSection();
    if (activeTab === 'listing') return ListingsSection();
    if (activeTab === 'food') return FoodItemsSection();
    if (['all', 'pending', 'approved'].includes(activeTab)) return ProductsSection();
    return null;
  };

  return (
    <div className="bg-slate-950 min-h-screen text-white font-sans">
      <ToastContainer position="top-right" autoClose={3000} theme="dark" pauseOnHover />
      
      {/* Delete Confirmation Modal */}
      {userToDelete && (
        <DeleteConfirmationModal
          user={userToDelete}
          onConfirm={() => handleDeleteUser(userToDelete.id)}
          onCancel={() => setUserToDelete(null)}
          isDeleting={isDeleting}
        />
      )}

      {DashboardHeader()}
      {NavigationTabs()}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {renderContent()}
      </div>
    </div>
  );
};

export default AdminProductForm;